import React from "react";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import {errorMessages} from "../../../../helpers/errorMessage";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {CountryService} from "../../../../common/services/countryService";
import {CountryModel} from "../../../../common/models/country";
import {routingAddresses} from "../../../../helpers/routing";
import {FerryService} from "../../../services/ferryService";
import {withRouter, RouteComponentProps} from "react-router";
import {Api} from "../../../../helpers/api";
import i18n from "../../../../i18n/i18n";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {FerryModel} from "../../../models/ferry";
import Form from "../../../../common/components/shared/form/form";
import {Utils} from "../../../../helpers/utils";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import {ShipownerModel} from "../../../models/shipowners";
import {ShipownerService} from "../../../services/shipownerService";
import Loader from "../../../../common/components/shared/loader/loader";

interface FerryFormState {
    countries: CountryModel[];
    shipowners: ShipownerModel[];
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    countriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    shipownersResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface FerryFormProps extends RouteComponentProps {
    ferryData?: FerryModel | null;
    isUpdate: boolean;
}

class FerryForm extends React.Component <FerryFormProps, FerryFormState> {
    private ferryNo: ValidatedValue<string>;
    private ferryName: ValidatedValue<string>;
    private flagStateImoCode: ValidatedValue<string>;
    private shipownerId: ValidatedValue<string>;

    constructor(props: any) {
        super(props);

        const ferryValidators = [{
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        }];

        this.ferryNo = new ValidatedValue("", ferryValidators);
        this.ferryName = new ValidatedValue("", ferryValidators);
        this.flagStateImoCode = new ValidatedValue("", ferryValidators);
        this.shipownerId = new ValidatedValue("", ferryValidators);

        const correctFerryNoValidators = 7;
        const ferryImoCodeValidators = [{
            name: "ferryNoLengthIsCorrect",
            fn: () => Validator.maxLength(this.ferryNo.value, correctFerryNoValidators),
            errorMessage: () => i18n.t("dictionaries.ferries.errors.ferryNoLengthIsCorrect", {number: correctFerryNoValidators})
        }];

        this.ferryNo.addValidators(ferryImoCodeValidators);

        this.state = {
            countries: [],
            isSubmitButtonBlocked: false,
            response: undefined,
            shipowners: [],
            shipownersResponse: undefined,
            countriesResponse: undefined
        };
    }

    async componentDidMount() {
        await this.getCountries();
        await this.getShipowners();

        this.setFerryData();
    }

    render() {
        const serviceFailed = !Api.isSuccess(this.state.countriesResponse) || !Api.isSuccess(this.state.shipownersResponse);
        if (serviceFailed || (this.state.countries.length > 0 && this.state.shipowners.length > 0)) {
            return (
                <Form
                    renderError={this.renderErrors()}
                    renderContent={this.renderContent()}
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    backAddress={routingAddresses.dictionaries.ferries.list}
                    submit={this.submit.bind(this)}
                />
            );
        } else {
            return <Loader />;
        }
    }

    protected renderErrors() {
        if(!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage()}
                    translatedFields={{
                        number: i18n.t("dictionaries.ferries.httpErrors.number"),
                        name: i18n.t("dictionaries.ferries.httpErrors.name")
                    }}
                    translatedFieldsForEmpty={{
                        number: i18n.t("dictionaries.ferries.httpErrors.numberForEmpty"),
                        name: i18n.t("dictionaries.ferries.httpErrors.nameForEmpty"),
                        flagStateName: i18n.t("dictionaries.ferries.httpErrors.flagStateNameForEmpty"),
                        flagStateImoCode: i18n.t("dictionaries.ferries.httpErrors.flagStateNameForEmpty"),
                        shipowner: i18n.t("dictionaries.ferries.httpErrors.shipownerForEmpty")
                    }}
                    object={i18n.t("dictionaries.ferries.httpErrors.object")}
                />
            );
        }
    }

    protected renderContent() {
        return (
            <>
                <TextInput label={i18n.t("dictionaries.ferries.headers.number")} validatedValue={this.ferryNo}
                    testId="ferry-no"/>
                <TextInput label={i18n.t("dictionaries.ferries.headers.name")} validatedValue={this.ferryName}
                    testId="ferry-name"/>
                <DropDown label={i18n.t("dictionaries.ferries.headers.flagStateName")}
                    validatedValue={this.flagStateImoCode} items={this.countriesToDropDownMap}
                    testId="ferry-flag-state"
                    error={!Api.isSuccess(this.state.countriesResponse) ? i18n.t("errorMessages.countriesErrorSuffix") : ""}
                    reloadFunc={() => this.getCountries()}
                />
                <DropDown label={i18n.t("dictionaries.ferries.headers.shipownerName")}
                    validatedValue={this.shipownerId} items={this.shipownersToDropDownMap}
                    testId="ferry-shipowner"
                    error={!Api.isSuccess(this.state.shipownersResponse) ? i18n.t("errorMessages.shipownersErrorSuffix") : ""}
                    reloadFunc={() => this.getShipowners()}
                />
            </>
        );
    }

    private getErrorMessage() {
        return this.props.isUpdate
            ? i18n.t("dictionaries.ferries.errors.modifyErrorSuffix")
            : i18n.t("dictionaries.ferries.errors.addErrorSuffix");
    }

    public get countriesToDropDownMap() {
        return DropDownUtils.listToDropDownMap(this.state.countries, "imoCode");
    }

    public get shipownersToDropDownMap() {
        return DropDownUtils.listToDropDownMap(this.state.shipowners);
    }

    public get isValid() {
        this.ferryNo.validate();
        this.ferryName.validate();
        this.flagStateImoCode.validate();
        this.shipownerId.validate();

        return this.ferryNo.isValid && this.ferryName.isValid && this.flagStateImoCode.isValid && this.shipownerId.isValid;
    }

    private setFerryData() {
        const flagStateName = this.props.ferryData?.flagStateName;
        const flagStateImoCode = this.state.countries.find((x) => x.name === flagStateName)?.imoCode || "";

        this.ferryNo.setValue(this.props.ferryData?.number || "");
        this.ferryName.setValue(this.props.ferryData?.name || "");
        this.flagStateImoCode.setValue(flagStateImoCode || "");
        this.shipownerId.setValue(this.props.ferryData?.shipowner?.id || "");
    }

    private async submit() {
        this.setState({response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyFerry();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.dictionaries.ferries.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyFerry(){
        if (this.props.isUpdate && this.props.ferryData?.id) {
            this.setState({
                response: await FerryService.modifyFerry({
                    number: this.ferryNo.value.trim(),
                    name: this.ferryName.value.trim(),
                    flagStateImoCode: this.flagStateImoCode.value,
                    shipownerId: this.shipownerId.value.trim()
                }, this.props.ferryData?.id)
            });
        } else {
            this.setState({
                response: await FerryService.addFerry({
                    number: this.ferryNo.value.trim(),
                    name: this.ferryName.value.trim(),
                    flagStateImoCode: this.flagStateImoCode.value,
                    shipownerId: this.shipownerId.value.trim()
                })
            });
        }
    }

    private async getShipowners() {
        const shipownersResponse = await ShipownerService.getAll();

        this.setState({
            shipowners: (shipownersResponse)?.data?.data || [],
            shipownersResponse
        });
    }

    private async getCountries() {
        const countriesResponse = await CountryService.getAll();

        this.setState({
            countries: (countriesResponse)?.data?.data || [],
            countriesResponse
        });
    }
}

export default withRouter(FerryForm);
